import { memo, useCallback } from "react";
import { Dialog, Pane, Paragraph } from "evergreen-ui";
import { useForm, Controller } from "react-hook-form";
import { useObservable } from "rxsupport";
import { BehaviorSubject, Subject } from "rxjs";
import {
  DecisionOption,
  ReferralSuggestionDecisionInput,
} from "offer/hook/useHigherPrioritySuggestion$";

type HigherPrioritySuggestionModalProps = {
  show$: BehaviorSubject<boolean>;
  confirm$: Subject<ReferralSuggestionDecisionInput | null>;
};

const MODAL_WIDTH = 800;

const DECISION_OPTIONS: DecisionOption[] = [
  DecisionOption.HigherPriority,
  DecisionOption.Both,
  DecisionOption.Current,
];

export const HigherPrioritySuggestionDialog = memo(
  function HigherPrioritySuggestionModal({
    show$,
    confirm$,
  }: HigherPrioritySuggestionModalProps) {
    const show = useObservable(show$, false);

    const onClose = useCallback(() => {
      show$.next(false);
    }, [show$]);

    const onCancel = useCallback(() => {
      // this will reset the suggestion workflow
      // keep this separate from the general onClose
      confirm$.next(null);

      show$.next(false);
    }, [show$, confirm$]);

    const onYes = useCallback(
      (decision: ReferralSuggestionDecisionInput) => {
        confirm$.next(decision);
        show$.next(false);
      },
      [show$, confirm$],
    );

    const {
      control,
      handleSubmit,
      watch,
      formState: { errors },
    } = useForm<ReferralSuggestionDecisionInput>({
      defaultValues: {
        decision: DecisionOption.HigherPriority,
        declineReason: "",
      },
    });

    const selectedDecision = watch("decision");
    const shouldShowDeclineReason = selectedDecision === DecisionOption.Current;

    return (
      <Dialog
        isShown={show}
        title="High Priority Client Alert"
        width={MODAL_WIDTH}
        hasClose={false}
        onCloseComplete={onClose}
        contentContainerProps={{
          textAlign: "left",
          padding: 20,
        }}
        footer={() => (
          <Pane width={MODAL_WIDTH - 32} className="flex justify-center">
            <button
              type="button"
              className="px-5 py-2 mr-5 focus:outline-none bg-white text-green-600 border border-green-600 hover:bg-green-50 disabled:opacity-50"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              form="suggestion-form"
              className={`px-5 py-2 bg-green-600 text-white hover:bg-green-700 disabled:opacity-60`}
            >
              Submit Offer
            </button>
          </Pane>
        )}
      >
        <div>
          <Paragraph className="text-gray-600 mb-8 text-sm" marginBottom="8">
            CCS has a higher priority client that requires placement, would you
            consider changing your offer to the client that has been waiting for
            a longer period of time; or are you able to accommodate both
            clients?
          </Paragraph>

          <form
            id="suggestion-form"
            onSubmit={handleSubmit(onYes)}
            className="max-w-md space-y-6"
          >
            <div className="space-y-2">
              <div className="flex align-middle items-center gap-4 px-8">
                <label
                  className="font-semibold w-64 flex-shrink-0"
                  htmlFor="suggestion-decision"
                >
                  Submit offer for:
                </label>

                <Controller<ReferralSuggestionDecisionInput>
                  name="decision"
                  control={control}
                  rules={{ required: "Please select an option" }}
                  render={({ field }) => (
                    <select
                      {...field}
                      id="suggestion-decision"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      style={{ minWidth: "400px" }}
                    >
                      {DECISION_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                />
              </div>
              {errors.decision && (
                <span className="text-red-500 text-sm">
                  {errors.decision.message}
                </span>
              )}
            </div>

            {shouldShowDeclineReason && (
              <div className="space-y-2">
                <div className="flex align-middle items-center gap-4 p-8">
                  <label
                    className="font-semibold w-64 flex-shrink-0"
                    htmlFor="suggestion-decline-reason"
                  >
                    Reason for not selecting longer pending client:
                  </label>
                  <Controller<ReferralSuggestionDecisionInput>
                    name="declineReason"
                    control={control}
                    rules={{
                      required: shouldShowDeclineReason
                        ? "Please enter reason for declining longer pending client"
                        : false,
                    }}
                    render={({ field }) => (
                      <textarea
                        {...field}
                        id="suggestion-decline-reason"
                        rows={4}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Please provide your reason..."
                        style={{ minWidth: "400px" }}
                      />
                    )}
                  />
                </div>
                {errors.declineReason && (
                  <span className="text-red-500 text-sm">
                    {errors.declineReason.message}
                  </span>
                )}
              </div>
            )}
          </form>
        </div>
      </Dialog>
    );
  },
);
